import React from 'react';
import { useLocation } from 'wouter';

import { ACCOUNT_PATHS } from '@/lib/accountPaths';
import {
  ACCOUNT_SITE_DOMAIN,
  ACCOUNT_SITE_PRICES,
} from '@/pages/account/accountFixture';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import { accountCount, accountMoney, isAccountOwner } from '@/pages/account/accountModel';
import {
  AccountCard,
  CardColumn,
  Muted,
  NotYetButton,
  Row,
  SmallButton,
  StatusChip,
} from '@/pages/account/primitives';
import { SettingsPageShell } from './SettingsPageShell';
import { SETTINGS_CARDS } from './settingsSearchIndex';
import { SETTINGS_PRO_WHY } from './SettingsCrm';

/**
 * AR-390 — Website Settings.
 *
 * D-ACCT-38 (Jason 2026-09-18) — two panels, each the door to the app that
 * holds the setting: the website is built and edited in WorldVia Sites, and
 * which suppliers' offers appear on it is set in WorldVia PRO. Neither panel
 * edits anything here, so neither card has an Edit.
 *
 * Email+ left this page: it is Marketing's, and explicitly not this ticket's.
 *
 * AR-391 — the Sites card reads `user_website_accounts` (AR-330): has-account
 * is whether a row exists, and status, domain and plan are the row's own
 * columns, the price the Stripe subscription's. With no row the card is the
 * no-account state and says so rather than showing a sample site. Dynamic
 * Promotions has NO source in AIVIA — it is managed in WorldVia PRO today —
 * so on a live account its rows say that instead of the sample's figures.
 *
 * BACK-END REQUIREMENT: single sign-on deep links into the Sites editor and
 * the PRO Dynamic Promotions page. No address is on file for either, so each
 * button is inert and says what it waits on.
 */

const SITES_WHY =
  'Single sign-on into the Sites editor arrives with the build — Member Success opens it with you meanwhile';

const PRO_MANAGED = 'Managed in WorldVia PRO';

export const SettingsWebsite: React.FC = () => {
  const { record, role, sites, isSettingsLive } = useAccountRecord();
  const [, navigate] = useLocation();
  const w = record.web;

  // The sample site while the module shows the sample record; the member's
  // own row once it answers. Every field below reads from this one place.
  const site = sites ?? {
    hasAccount: true,
    status: 'Live',
    domain: ACCOUNT_SITE_DOMAIN,
    url: `https://${ACCOUNT_SITE_DOMAIN}`,
    plan: w.plan,
    price: ACCOUNT_SITE_PRICES[w.plan] ?? null,
  };

  return (
    <SettingsPageShell page="website">
      <CardColumn>
        <AccountCard name={SETTINGS_CARDS.sites.name}>
          {site.hasAccount ? (
            <>
              <Row label="Status" chip={<StatusChip status={site.status} />} />
              <Row label="Domain" value={site.domain} />
              <Row label="Plan" value={site.plan} />
              {/* Null is "Stripe could not answer" (the reason is on the
                  unbound list), which must not look like a blank price. */}
              <Row
                label="Price"
                value={
                  site.price === null ? (
                    <Muted>Not available right now</Muted>
                  ) : (
                    `${accountMoney(site.price)} / mo`
                  )
                }
                last
              />
              <p className="mt-2.5 text-[14px] text-gray-500 dark:text-gray-400">
                Your website is built and edited in WorldVia Sites, outside this app.
              </p>
            </>
          ) : (
            <>
              <Row label="Status" chip={<StatusChip status={site.status} />} last />
              <p className="mt-2.5 text-[14px] text-gray-500 dark:text-gray-400">
                You do not have a WorldVia Sites website yet. Websites are built and
                edited in WorldVia Sites, outside this app.
              </p>
            </>
          )}
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <NotYetButton note={SITES_WHY}>
              {site.hasAccount
                ? 'Manage your website in WorldVia Sites ↗'
                : 'Set up a website in WorldVia Sites ↗'}
            </NotYetButton>
            {site.hasAccount && site.url ? (
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-gray-200 bg-white px-3 py-1 text-[13.5px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
              >
                View website
              </a>
            ) : null}
            {isAccountOwner(role) ? (
              <SmallButton onClick={() => navigate(ACCOUNT_PATHS.billing)}>
                Manage subscription
              </SmallButton>
            ) : null}
          </div>
        </AccountCard>

        <AccountCard name={SETTINGS_CARDS.promos.name}>
          {isSettingsLive ? (
            <>
              {/* No source in AIVIA: listed, not faked (AR-391). */}
              <Row label="Status" value={<Muted>{PRO_MANAGED}</Muted>} />
              <Row label="Suppliers selected" value={<Muted>{PRO_MANAGED}</Muted>} last />
            </>
          ) : (
            <>
              <Row label="Status" chip={<StatusChip status="On" />} />
              <Row
                label="Suppliers selected"
                value={accountCount(w.promos, 'supplier', 'suppliers')}
                last
              />
            </>
          )}
          <p className="mt-2.5 text-[14px] text-gray-500 dark:text-gray-400">
            Which suppliers' offers appear on your website is set in WorldVia PRO.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <NotYetButton note={SETTINGS_PRO_WHY}>
              Manage Dynamic Promotions in WorldVia PRO ↗
            </NotYetButton>
          </div>
        </AccountCard>
      </CardColumn>
    </SettingsPageShell>
  );
};

export default SettingsWebsite;
